import React, { useState } from 'react';
import { Modal, ModalContent, ModalBody, Button } from '@heroui/react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { appReviewApi } from '@lib/services/appReview/appReviewApi';
import { useAuthStore } from '@stores/authStore';
import AppLogo from '../../assets/Abstract.png';

interface AppReviewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number) => void;
  notificationId?: string;
}

export const AppReviewPopup: React.FC<AppReviewPopupProps> = ({ isOpen, onClose, onSubmit, notificationId }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const { user } = useAuthStore();

  const handleSubmit = async () => {
    if (rating === 0 || !notificationId || !user?.id) return;
    
    setIsSubmitting(true);
    
    try {
      // Call API to submit review
      const result = await appReviewApi.submitReview(user.id, notificationId, rating);
      
      if (result.success) {
        // Call parent onSubmit callback
        onSubmit(rating);
        
        // Close popup
        onClose();
        
        // Redirect to Shopify app store
        window.open('https://apps.shopify.com/yep-ai-chatbot?locale=zh-TW', '_blank');
      } else {
        console.error('Failed to submit review:', result.message);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotNow = async () => {
    if (!notificationId || !user?.id) return;
    
    setIsCancelling(true);
    
    try {
      // Call API to cancel review
      const result = await appReviewApi.cancelReview(user.id, notificationId);
      
      if (result.success) {
        console.log('Review cancelled successfully');
      } else {
        console.error('Failed to cancel review:', result.message);
      }
    } catch (error) {
      console.error('Error cancelling review:', error);
    } finally {
      setIsCancelling(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      classNames={{
        base: "bg-white rounded-3xl shadow-2xl w-[calc(100vw-32px)] lg:!w-[678px] h-auto lg:!h-[220px] !max-w-none mx-4 lg:mx-0",
        body: "p-0",
        wrapper: "flex items-center justify-center",
      }}
      hideCloseButton={true}
    >
      <ModalContent className="w-full lg:!w-[578px] h-auto lg:!h-[220px] !max-w-none">
        <ModalBody>

          {/* Main content area */}
          <div className="px-4 py-5 lg:px-6 lg:py-6 bg-white rounded-b-3xl">
            <div className="flex flex-col lg:flex-row items-center lg:items-start mb-4 text-center lg:text-left">
              {/* App Icon */}
              <div className="w-16 h-16 lg:w-20 lg:h-20 bg-[#6B33FF] rounded-2xl flex items-center justify-center mb-3 lg:mb-0 lg:mr-6">
                <img
                  src={AppLogo}
                  alt="YepAI Logo"
                  className="w-10 h-10 lg:w-14 lg:h-14 object-cover rounded-xl"
                />
              </div>

              {/* Title and description */}
              <div className="flex-1">
                <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-1 leading-tight">Enjoying YepAI?</h2>
                <p className="text-gray-600 text-xs lg:text-sm">Click a star to rate it on the App Store.</p>
              </div>
            </div>

            {/* Star Rating - centered on mobile */}
            <div className="flex justify-center mb-4 lg:hidden">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110 mx-0.5"
                  disabled={isSubmitting || isCancelling}
                >
                  {star <= (hoveredRating || rating) ? (
                    <StarIcon className="w-8 h-8 text-yellow-400" />
                  ) : (
                    <StarOutlineIcon className="w-8 h-8 text-gray-300" />
                  )}
                </button>
              ))}
            </div>

            {/* Buttons Row - stacked on mobile */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-3 lg:gap-0 lg:mt-12">
              {/* Not now button */}
              <Button
                variant="bordered"
                size="lg"
                className="w-full lg:w-auto px-8 py-3 border-gray-300 text-gray-700 bg-white rounded-[8px] text-sm lg:text-base border-1 h-11 lg:h-12 order-2 lg:order-1"
                onPress={handleNotNow}
                isLoading={isCancelling}
                isDisabled={isSubmitting || isCancelling}
              >
                Not now
              </Button>

              {/* Star Rating - hidden on mobile, shown on desktop */}
              <div className="hidden lg:flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="focus:outline-none transition-transform hover:scale-110"
                    disabled={isSubmitting || isCancelling}
                  >
                    {star <= (hoveredRating || rating) ? (
                      <StarIcon className="w-8 h-8 text-yellow-400" />
                    ) : (
                      <StarOutlineIcon className="w-8 h-8 text-gray-300" />
                    )}
                  </button>
                ))}
              </div>

              {/* Submit button */}
              <Button
                size="lg"
                className="w-full lg:w-auto px-8 py-3 bg-[#6B33FF] text-white font-semibold rounded-[8px] text-sm lg:text-base h-11 lg:h-12 order-1 lg:order-3"
                onPress={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={rating === 0 || isSubmitting || isCancelling}
              >
                Submit
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};